import { apiClient } from './base';

// ---------------------------------------------------------------------------
// Pour interfaces
// ---------------------------------------------------------------------------

export interface Pour {
  PourID: string;
  PourName: string;
  ProjectID?: string;
  ProjectName?: string;
  ProductID?: string;
  ProductCode?: string;
  ProductDescription?: string;
  ProductStrengthGrade?: number;
  Functionality?: string;
  MonitoringFunctions?: string;
  MaturityMethod?: string;
  Location?: string;
  PourVolume?: number;
  PourDate?: string;
  PourStatus?: string;
  CompanyName?: string;
  PourCode?: string;
  LoggerStart?: string;
  LoggerEnd?: string;
  LoggerDuration?: number;
  TimeZone?: string;
  TimeZoneIANA?: string;
  StopType?: string;
  XHours?: number;
  BaseCuringTemp?: number;
  SupplierPlant?: string;
  SupplierPlantID?: string;
  CalibrationID?: string;
  CalibrationName?: string;
  SetupBy?: string;
  Started?: string;
  Finished?: string;
  Levels?: string;
  ProjectRef?: string;
  Code?: string;
  /** Completed pours auto-lock; admins can unlock/re-lock. */
  Locked?: boolean;
  // Health roll-up for the control-panel list (active pours only; backend
  // DashboardService.get_health_summaries).
  /** Monitoring locations on the pour. */
  LoggerCount?: number;
  /** Locations with a sensor or comms issue. */
  WarningCount?: number;
  /** Any channel that should be logging is Offline. */
  AnyStale?: boolean;
  /** In Progress but not started yet: shown as "Scheduled" with StartsAt
   *  (the earliest planned start, naive UTC). PourStatus is unchanged. */
  Scheduled?: boolean;
  StartsAt?: string;
  /** The pour has a weather station (site weather, never counted among
   *  the locations above), and it should be reporting and is not. */
  HasWeatherStation?: boolean;
  WeatherStationStale?: boolean;
  // Site state for the phone Control Panel card, sent only when the list is
  // asked for with health_detail=true (In Progress rows only; null or absent
  // otherwise, and on older backends).
  /** Locations whose newest reading is outside a temperature or
   *  differential limit (stale readings excluded); null when no limits
   *  apply. */
  OverLimitCount?: number | null;
  /** Channels that should be logging and are Offline (AnyStale ==
   *  OfflineCount > 0). */
  OfflineCount?: number | null;
  /** Locations whose logging has ended. */
  EndedCount?: number | null;
  /** The newest reading of any location, naive UTC. */
  NewestReadingAt?: string | null;
  /** Hottest newest reading over the concrete temperature channels; null
   *  for pours with only ambient or weather hardware. */
  MaxCurrentTemp?: number | null;
  /** Worst differential now over the configured pairs, and its limit. */
  MaxDifferentialNow?: number | null;
  MaxDifferentialLimit?: number | null;
  /** The earliest location concrete start (EAStart), naive UTC. */
  ConcreteStart?: string | null;
  /** Live ambient reading (deg C, %RH): the hottest ConXenviro / ambient
   *  T&H temperature channel that is logging and not Offline, and the
   *  humidity of that same logger (else the newest of any). A weather
   *  station is the fallback when no ambient logger has a live reading. */
  AmbientTemp?: number | null;
  AmbientHumidity?: number | null;
  /** How the lowest location is read: 'strength' when the pour's curve has
   *  calibration samples, else 'maturity'. Null without a maturity
   *  location (for example a ConXenviro-only pour). */
  MaturityBasis?: 'strength' | 'maturity' | null;
  /** Maturity of the lowest concrete location: the stored value in the
   *  pour's own method (TTF deg C hrs for Nurse Saul, EA hrs for
   *  Arrhenius). */
  LowestEA?: number | null;
  /** Unit of LowestStrength and a strength NextTargetValue: the company
   *  StrengthFormat, as the dashboard ('MPa', 'Psi' or 'N/mm²'). Set
   *  whenever MaturityBasis is. */
  StrengthUnit?: string | null;
  /** Its strength in StrengthUnit (strength basis only). When outside
   *  the tested range it is S(min) / S(max) and LowestStrengthBound says
   *  which: show "< 3.3" / "> 41.0". */
  LowestStrength?: number | null;
  LowestStrengthBound?: 'below' | 'above' | null;
  /** That location's next unmet target: name, and strength (strength basis)
   *  or maturity (maturity basis). Null when all are reached or none set. */
  NextTargetName?: string | null;
  NextTargetValue?: number | null;
  /** Its maturity threshold on either basis: the progress bar is LowestEA
   *  over it. */
  NextTargetMaturity?: number | null;
  /** That location's targets reached and in all (0 / 0 without targets). */
  TargetsReached?: number | null;
  TargetsTotal?: number | null;
  [key: string]: any;
}

export interface PourDetail extends Pour {
  TargetGroupID?: string;
  TargetGroupName?: string;
  LimitGroupID?: string;
  WeatherStationID?: string;
  WeatherStationName?: string;
  EvaLimitGroupID?: string;
  EvaporationLimitName?: string;
  SpecifiedTemp?: number;
  ActivationEnergy?: number;
  DatumTemp?: number;
  PreSetStopTime?: string;
  AcceptDefaultTarget?: string;
  Archived?: boolean;
  /** True when MonitoringFunctions was inferred server-side for a legacy-app
   *  pour that never recorded them (stored once the pour is saved). */
  MonitoringFunctionsInferred?: boolean;
  /** True once the pour has a temperature reading: the mix design,
   *  calibration and maturity inputs can no longer change. */
  MaturityInputsLocked?: boolean;
  /** The pour's company; differs from the viewer's own for superadmins. */
  CompanyID?: string;
  /** The pour's own batch plant: null when it has none and shows its mix
   *  design's (SupplierPlantID / SupplierPlant are what it shows). Absent
   *  on older responses. */
  PourSupplierPlantID?: string | null;
  /** The mix design's plant, shown for a pour with none of its own. */
  ProductSupplierPlant?: string | null;
  /** ASTM C1074 9.2: the pour's maturity basis against its calibration's,
   *  from the stored pour values. Absent on older responses. */
  calibration_check?: CalibrationCheck | null;
}

/** A maturity basis: the function and inputs maturity is computed with
 *  (backend services/calibration_basis._basis). */
export interface Basis {
  /** Where it came from: 'pour' | 'snapshot' | 'calibration_pour' |
   *  'equation' | 'mix_design' (assumed). */
  source: string;
  /** 'Nurse Saul' | 'Arrhenius' */
  method: string;
  unit: string;
  datum_temp: number | null;
  datum_is_default?: boolean;
  /** J/mol. */
  activation_energy: number | null;
  /** K (activation energy / 8.31). */
  q_value: number | null;
  q_is_default?: boolean;
  specified_temp: number | null;
  equation_id?: string | null;
  equation_name?: string | null;
  pour_id?: string | null;
  pour_name?: string | null;
}

/** The differences a CalibrationCheck can name. */
export type BasisDifference = 'method' | 'datum_temp' | 'activation_energy' | 'specified_temp';

/** ASTM C1074 9.2: a pour must compute maturity with the same function and
 *  inputs as its calibration was developed with. 'none' = the pour has no
 *  calibration (maturity-only targets). */
export interface CalibrationCheck {
  status: 'match' | 'differs' | 'mismatch' | 'unknown' | 'none';
  /** BasisDifference values: the inputs that differ. */
  differences: string[];
  pour_basis: Basis | null;
  calibration_basis: Basis | null;
  calibration_id: string | null;
  calibration_name: string | null;
  /** One plain sentence for the UI. */
  text: string;
}

export interface PourCreate {
  PourName: string;
  ProjectID: string;
  ProductID: string;
  SupplierPlantID?: string;
  MaturityMethod: string;
  Functionality?: string;
  MonitoringFunctions?: string;
  BaseCuringTemp?: number;
  StopType: string;
  XHours?: number;
  PreSetStopTime?: string;
  TargetGroupID?: string;
  LimitGroupID?: string;
  CalibrationID?: string;
  EvaLimitGroupID?: string;
  WeatherStationID?: string;
  SpecifiedTemp?: number;
  ActivationEnergy?: number;
  DatumTemp?: number;
  Location?: string;
  ProjectRef?: string;
  PourVolume?: number;
  Levels?: string;
  Started?: string;
  Finished?: string;
  // Wizard bundle: include these when the create form collects locations
  // and contacts pre-save (single transaction).
  locations?: PourLocation2Create[];
  contacts?: Pour2ContactCreate[];
  /** The user confirmed the pour and the calibration use different maturity
   *  inputs (CalibrationCheck 'differs'). Only asked of a pour that computes
   *  maturity (Concrete Maturity). Not stored. */
  acknowledge_basis_difference?: boolean;
}

export interface PourUpdate {
  PourName?: string;
  ProjectID?: string;
  ProductID?: string;
  /** null clears it (as do the other `| null` fields: the pour then has no
   *  value, as on create). */
  SupplierPlantID?: string | null;
  MaturityMethod?: string;
  Functionality?: string;
  MonitoringFunctions?: string;
  BaseCuringTemp?: number;
  StopType?: string;
  XHours?: number;
  PreSetStopTime?: string;
  /** null clears it (None picked, or a corrected mix design that has none). */
  TargetGroupID?: string | null;
  /** null clears it (None picked: the pour then has no group, as on create). */
  LimitGroupID?: string | null;
  /** null clears it (a corrected mix design that has none). */
  CalibrationID?: string | null;
  EvaLimitGroupID?: string | null;
  WeatherStationID?: string | null;
  SpecifiedTemp?: number;
  ActivationEnergy?: number;
  DatumTemp?: number;
  Location?: string;
  ProjectRef?: string | null;
  PourVolume?: number;
  Levels?: string | null;
  Started?: string;
  Finished?: string | null;
  /** See PourCreate.acknowledge_basis_difference. Read only for a pour that
   *  computes maturity (Concrete Maturity), when the save changes the mix
   *  design, the calibration or the maturity inputs (before the first
   *  reading), or makes the pour compute maturity (Concrete Maturity added,
   *  before or after the first reading). Any other pour is never refused on
   *  the calibration basis. */
  acknowledge_basis_difference?: boolean;
}

export interface PourCreateResponse {
  PourID: string;
  PourCode: string;
  message: string;
}

export interface PourListResponse {
  pours: Pour[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface PourPageQuery {
  page: number;
  pageSize: number;
  status?: string;
  search?: string;
  archived?: boolean;
  /** Only this mix design's pours. */
  productId?: string;
  /** false skips the logger health roll-up (warnings, stale data). */
  health?: boolean;
  /** true adds the site state fields (OverLimitCount, OfflineCount, ...)
   *  to In Progress rows. Sent only when set (the phone Control Panel). */
  healthDetail?: boolean;
  /** Superadmin only: just this client's pours (the server ignores it for
   *  every other role). */
  companyId?: string;
}

export interface PourPageResponse {
  pours: Pour[];
  /** Pours matching the status and search filters. */
  total: number;
  /** Every pour in the list (live or archived) before the filters. */
  total_all: number;
  page: number;
  size: number;
}

export interface PourMapSummary {
  project_names: string[];
  latest_project_name: string | null;
}

// ---------------------------------------------------------------------------
// Sub-resource interfaces
// ---------------------------------------------------------------------------

export interface Pour2Target {
  ID: string;
  PourID: string;
  Purpose: string;
  Target: number;
  Strength?: number;
}

/** A decimal the API may send as text ("32.00") as a number; undefined when
 *  missing or not a number. */
const decimalValue = (value: unknown): number | undefined => {
  if (value == null || (typeof value === 'string' && value.trim() === '')) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

/** Pour2TargetResponse.Strength is a Decimal, which the API serialises as
 *  text ("32.00"): convert it (and Target) to numbers as the type says. */
export const toPour2Target = (raw: any): Pour2Target => ({
  ...raw,
  Target: decimalValue(raw?.Target) ?? raw?.Target,
  Strength: decimalValue(raw?.Strength),
});

export interface Pour2TargetCreate {
  Purpose: string;
  Target: number;
  Strength?: number;
}

export interface Pour2Limit {
  ID: string;
  PourID: string;
  MassConcreteDefaultsID: string;
  Criteria: string;
  Purpose: string;
  LimitsType: string;
  LimitsTemp: number;
  BackgroundColor?: string;
  TextColor?: string;
}

export interface Pour2LimitCreate {
  MassConcreteDefaultsID: string;
  Criteria: string;
  Purpose: string;
  LimitsType: string;
  LimitsTemp: number;
  BackgroundColor?: string;
  TextColor?: string;
}

export interface Pour2EvaporationLimit {
  ID: string;
  PourID: string;
  EvaporationLimitID: string;
  Purpose: string;
  LimitsType: string;
  LimitsRate: number;
  BackgroundColor?: string;
  TextColor?: string;
}

export interface Pour2EvaporationLimitCreate {
  EvaporationLimitID: string;
  Purpose: string;
  LimitsType: string;
  LimitsRate: number;
  BackgroundColor?: string;
  TextColor?: string;
}

export interface Pour2Contact {
  ID: string;
  PourID: string;
  ContactID: string;
  ContactName?: string;
  Email?: string;
  SendEmail: string;
  Alert: string;
}

export interface Pour2ContactCreate {
  ContactID: string;
  SendEmail?: string;
  Alert?: string;
}

export interface PourLocation2 {
  ID: string;
  PourID: string;
  LocationID: string;
  LocationDescription?: string;
  LoggerID: string;
  LoggerCode?: string;
  ChannelNo: number;
  StartType?: string;
  StopType?: string;
  MonitorType?: string;
  /** A Temperature Only channel of concrete hardware reading the air. */
  IsAmbient?: boolean;
  Details?: string;
  LogginStart?: string;
  LoggingEnd?: string;
  EAStart?: string;
  hasWeather?: boolean;
  TankID?: string;
  LoggingStarted?: string;
  LoggingFinished?: string;
  hasTempDiffLimit?: boolean;
  has_temp_limit?: boolean;
  temp_diff_location_ids?: string[];
  /** Sensor data type of this channel ('TEMPERATURE' | 'HUMIDITY';
   *  undefined = unknown/legacy). Keeps humidity channels out of the
   *  differential-partner picker. */
  channel_type?: string | null;
}

export interface TargetStrengthOverride {
  pour2_target_id: string;
  strength: number;
}

export interface PourLocation2Create {
  LocationID: string;
  LocationDescription?: string;
  LoggerID: string;
  ChannelNo: number;
  StartType?: string;
  MonitorType?: string;
  /** A Temperature Only channel of concrete hardware reading the air. */
  IsAmbient?: boolean;
  Details?: string;
  LogginStart?: string;
  /** null on update clears a stored stop time; undefined leaves it untouched. */
  LoggingEnd?: string | null;
  EAStart?: string;
  hasWeather?: boolean;
  /** Tank serial number (matches LoggerInfo.SerialNumber, Type='08').
   *  Empty string clears any prior binding. */
  TankID?: string;
  target_strengths?: TargetStrengthOverride[];
  has_temp_limit?: boolean;
  temp_diff_location_ids?: string[];
}

export interface LocationTarget {
  id: string;
  pour2_target_id: string;
  purpose: string;
  target: number | null;
  strength: number | null;
}

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

export interface LookupChannel {
  no: number;
  /** 'TEMPERATURE' | 'HUMIDITY' | null (unknown / unpopulated port). */
  type?: string | null;
  /** Whether the port has a sensor id registered at all. */
  populated: boolean;
}

export interface LookupItem {
  id: string;
  name: string;
  /** Only populated for `projects` — IANA TZ from ProjectInfo.TimeZone, used
   *  by the pour setup form to convert datetime-local inputs into the
   *  project's local timezone. */
  timezone_iana?: string;
  /** Only populated for `loggers` — LoggerInfo.Type code ('01'=Agent, '02'=Sensor, '03'=ConXone, '07'=Weather, '08'=Tank, '09'=Digit Sensor, '10'=ConXenviro, '99'=Virtual simulator logger). */
  type?: string;
  /** Only populated for `loggers` — per-channel sensor info, used by the
   *  wizard to auto-create one location per real channel on multi-sensor
   *  devices (ConXenviro). */
  channels?: LookupChannel[];
  /** `loggers` and `tanks` — PourID of the active pour currently holding this
   *  device; null/undefined = available. Hardware is exclusive to one active
   *  pour (weather stations excepted), so pickers hide devices held by OTHER
   *  pours. */
  in_use_by_pour?: string | null;
}

export interface PourLookups {
  projects: LookupItem[];
  weather_stations: LookupItem[];
  loggers: LookupItem[];
  /** Type='08' tank loggers. id = SerialNumber (PourLocation.TankID convention). */
  tanks: LookupItem[];
  contacts: LookupItem[];
  mass_concrete_defaults: LookupItem[];
  evaporation_defaults: LookupItem[];
}

// ---------------------------------------------------------------------------
// Maturity / strength display (backend schemas/maturity_schemas.py)
// ---------------------------------------------------------------------------

/** How a pour's maturity values are named (MaturityDescriptorDTO). Only the
 *  exact method "Nurse Saul" is TTF; every other stored value is Arrhenius. */
export interface MaturityDescriptor {
  /** "Nurse Saul" | "Arrhenius" */
  method: string;
  kind: 'ttf' | 'ea';
  /** "TTF" | "EA" */
  index_short: string;
  /** "Temperature-Time Factor" | "Equivalent Age at 20 °C" */
  index_label: string;
  /** "°C·hrs" | "hrs" */
  unit: string;
  /** "Temperature-Time Factor (°C·hrs)" | "Equivalent Age at 20 °C (hrs)" */
  axis_title: string;
  datum_temp?: number | null;
  datum_is_default?: boolean;
  specified_temp?: number | null;
  activation_energy?: number | null;
  q_value?: number | null;
  q_is_default?: boolean;
}

/** One strength for display (StrengthReadingDTO). `display` never carries
 *  the unit: "24.3" | "< 3.3" | "> 41.0" | "NCS*" | "". */
export interface StrengthReading {
  /** 1 dp; S(min) / S(max) when outside the tested range. */
  value: number | null;
  bound: 'below' | 'above' | null;
  no_curve: boolean;
  no_samples: boolean;
  display: string;
  tooltip: string | null;
}

/** A calibration's tested maturity range and plot window (CalibratedRangeDTO). */
export interface CalibratedRange {
  min_m: number | null;
  max_m: number | null;
  /** S(min_m), 1 dp, never below 0. */
  s_min: number | null;
  /** S(max_m), 1 dp. */
  s_max: number | null;
  plot_min_m: number | null;
  plot_max_m: number | null;
  has_curve: boolean;
  has_samples: boolean;
}

/** Two consecutive readings further apart than ASTM C1074 7.3 allows (30
 *  minutes in the first 48 hours after the concrete start, 1 hour after).
 *  Only flagged; the maturity is not changed (ReadingIntervalIssueDTO). */
export interface ReadingIntervalIssue {
  /** The earlier reading (naive UTC). */
  start: string;
  /** The next reading. */
  end: string;
  /** The gap, 1 dp. */
  minutes: number;
  /** 30 or 60: the limit the gap breaks. */
  limit_minutes: number;
}

// ---------------------------------------------------------------------------
// Dashboard interfaces (unchanged)
// ---------------------------------------------------------------------------

export interface PourSummary {
  id: string;
  name: string;
  /** The Pour ID people see (PourCode, e.g. "FC-2609-0023"); absent on
   *  older responses. */
  pour_code?: string | null;
  project_name?: string;
  description?: string;
  mix_design?: string;
  started_at?: string;
  status?: string;
  /** The company's strength unit (MPa / Psi / N/mm²). */
  strength_unit?: string;
  functionality?: string;
  /** Comma-joined granular monitoring functions (e.g. "Temperature Only,
   *  Concrete Maturity"). Drives which Location Details graphs are shown. */
  monitoring_functions?: string;
  /** The stored method. Only "Nurse Saul" is TTF (°C·hrs); every other
   *  value ("Arrhenius", "equivalent-age", "", a group ID) is EA (hrs). */
  maturity_method?: string;
  /** Whether a calibration is linked to the pour. It says nothing about
   *  strength: use has_curve (utils/maturityDisplay.pourHasCurve). */
  has_calibration?: boolean;
  /** The pour's maturity naming; absent on older responses (then derive
   *  it from maturity_method with utils/maturityDisplay.maturityMeta). */
  maturity?: MaturityDescriptor | null;
  /** Whether strength can be estimated and shown: the linked calibration has
   *  a usable curve (finite coefficients, B > 0) and calibration_check is not
   *  'mismatch'. False: no strength at all, only maturity, even when
   *  has_calibration is true. */
  has_curve?: boolean;
  /** The calibration's tested range; all null for a 'mismatch' pour. */
  calibrated_range?: CalibratedRange | null;
  /** ASTM C1074 9.2: the pour's maturity basis against its calibration's,
   *  from the stored pour values (legacy pours too). A 'mismatch' pour
   *  shows no strength anywhere (has_curve false), and its text says so. */
  calibration_check?: CalibrationCheck | null;
  /** PourInfo.Locked: a locked pour refuses edits and maturity resets
   *  until an admin unlocks it. */
  locked?: boolean;
  timezone?: string;
  timezone_iana?: string;
  /** In Progress but not started yet: every location's planned start is
   *  ahead and no reading has arrived. Shown as "Scheduled" with starts_at;
   *  status stays '1' (legacy needs it). Absent on older responses. */
  scheduled?: boolean | null;
  /** The earliest planned start of a scheduled pour, naive UTC. */
  starts_at?: string | null;
  /** The newest reading of any location, naive UTC. Sent with
   *  site_state=true; absent on older responses. */
  newest_reading_at?: string | null;
  /** The earliest location concrete start (EAStart), naive UTC. Sent with
   *  site_state=true; absent on older responses. */
  concrete_start?: string | null;
  /** The pour's own stop setting ('31' x_hours days, '7' x_hours hours,
   *  '22' preset_stop_time) and its logger start (LoggerStart, else the
   *  earliest location start), naive UTC, for the desktop "past planned
   *  end" banner (utils/pastPlannedEnd). Absent on older responses. */
  stop_type?: string | null;
  x_hours?: number | null;
  preset_stop_time?: string | null;
  logger_start?: string | null;
  /** The company's wind unit. Wind values always arrive in km/h; show them
   *  with utils/windSpeed. Absent on older responses (km/h). */
  wind_speed_unit?: 'km/h' | 'mph';
}

export interface Weather {
  temp?: number;
  humidity?: number;
  /** km/h, as stored. */
  wind_speed?: number;
  /** Time of the newest weather reading, naive UTC (site_state=true). */
  at?: string | null;
}

/** GET /pour/{id}/weather: the pour's station readings, oldest first, each
 *  [epoch ms UTC, temp °C, humidity %RH, wind km/h] (null when not sent). */
export interface WeatherSeries {
  points: [number, number | null, number | null, number | null][];
  since_applied?: number | null;
}

/** A location target. `achieved` is the engine's maturity stamp: the only
 *  rule for whether a target is met (strength never decides it). */
export interface Target {
  id?: string | null;
  name: string;
  /** Required strength in the company unit; null when unknown. */
  target_strength?: number | null;
  /** Maturity threshold (TTF in °C·hrs, or equivalent age in hrs). */
  target_maturity: number;
  achieved: boolean;
  achieved_at?: string | null;
  is_final?: boolean;
  /** The target strength lies outside the calibration's tested range
   *  (allowed, but flagged). A "below" / "above" flag counts as true. */
  outside_range?: boolean | 'below' | 'above' | null;
}

// Site state of a location, sent by GET /pour/{id}/dashboard?site_state=true
// (backend services/limit_state.py). Operators are comparison symbols:
// '>' | '>=' | '<' | '<='.

/** A location-bound temperature limit ('02' row): the profile bar, its
 *  caption and the over-limit verdict all use these. */
export interface TempLimit {
  /** Configured limit name (Purpose), e.g. "Max Temp". */
  name: string | null;
  operator: string;
  value: number;
  color: string | null;
}

/** A limit the location is outside of now. */
export interface LimitBreach {
  kind: 'temperature' | 'differential' | 'evaporation';
  limit_name: string | null;
  operator: string;
  limit_value: number;
  /** The value that breaks it: °C now, |ΔT| °C, or the evaporation rate. */
  value: number;
  /** Time of that value, naive UTC. */
  value_at: string | null;
  /** When this breach began (60-minute merge rule, 48 h look-back), naive
   *  UTC; null when unknown. */
  since: string | null;
  /** The breach began before the look-back: "for more than 48 h". */
  since_capped: boolean;
  color: string | null;
  /** The other end of a differential pair. */
  partner_id: string | null;
  partner_name: string | null;
}

/** The current differential to one configured partner location. */
export interface DifferentialNow {
  partner_id: string;
  partner_name: string | null;
  /** °C; null when the two newest readings are more than 15 min apart. */
  value: number | null;
  value_at: string | null;
  limit_value: number | null;
  limit_name: string | null;
  operator: string | null;
  over: boolean;
}

export interface LocationStatus {
  id: string;
  name?: string;
  logger_code: string;
  channel: number;
  /** Sensor data type of this channel ('TEMPERATURE' | 'HUMIDITY');
   *  undefined = unknown/legacy, treated as temperature. HUMIDITY channel
   *  values (current_temp/max_temp fields) are %RH, not °C. */
  channel_type?: string;
  /** LoggerInfo.Type device code ('01'=ConXagent, '02'=ConXsensor,
   *  '09'/'10'=ambient Temp & Humidity hardware). Ambient channels drop the
   *  limit gradient per channel, not per pour. */
  logger_type?: string | null;
  /** PourLocation2.MonitorType ('1'=Concrete Maturity, '2'=Temperature Only;
   *  null = legacy, treated as maturity). Groups a dual-purpose pour's
   *  location lists by monitoring function. */
  monitor_type?: string | null;
  /** A Temperature Only channel of concrete hardware reading the air:
   *  treated like an ambient Temp & Humidity channel. */
  is_ambient?: boolean;
  status: string;
  battery?: string;
  signal?: string;
  logging_started?: string;
  /** PourLocation.EAStart: the concrete start maturity is counted from
   *  (naive UTC); target times are measured from it. Absent on older
   *  responses. */
  ea_start?: string | null;
  /** PourLocation.LogginStart: the planned start from pour setup. Ahead of
   *  now with no reading yet, the location shows as Scheduled. */
  scheduled_start?: string | null;
  /** When the location's logging finishes or finished (the logger's own
   *  stop, else the location's end); null while open-ended. Once passed the
   *  location shows "Logging ended" instead of Offline. */
  logging_ended?: string | null;
  /** The newest reading's time (a weather station's comes from the weather
   *  track); absent before the first reading. */
  last_read?: string;
  current_temp?: number;
  max_temp?: number;
  current_ea?: number;
  current_strength?: number;
  /** Current strength for display (calibrated-range rule); absent on older
   *  responses, which only send current_strength. */
  strength?: StrengthReading | null;
  targets: Target[];
  /** Current maturity as a share of the final target, 0-100; null without targets. */
  maturity_progress?: number | null;
  /** The final target has been achieved. */
  targets_complete?: boolean;
  // Site state (site_state=true only; absent on older responses).
  temp_limits?: TempLimit[];
  has_temp_limits?: boolean;
  /** Outside a temperature or differential limit now. null: not evaluated
   *  (no limits, no reading, scheduled or ended, a site or humidity
   *  channel). */
  over_limit?: boolean | null;
  /** The newest reading is too old to judge (Offline): an over_limit from
   *  it does not count as outside limits now. */
  stale?: boolean;
  /** Worst first: temperature, then differential, then evaporation. */
  breaches?: LimitBreach[];
  /** Configured differential partners, worst first; [] when none. */
  differentials?: DifferentialNow[];
  /** Evaporation rate (kg/m²/h) of the chart's last closed 30-minute
   *  bucket, its time and whether it is over the evaporation limit. */
  evaporation_now?: number | null;
  evaporation_at?: string | null;
  evaporation_over?: boolean | null;
  /** When max_temp was read, naive UTC. */
  max_temp_at?: string | null;
  /** Battery percentage; null when the logger sent no code (unlike
   *  `battery`, which reads '0%' then). */
  battery_pct?: number | null;
}

export interface Attachment {
  name: string;
  url: string;
}

// ---------------------------------------------------------------------------
// Pour attachments (CRUD)
// ---------------------------------------------------------------------------

export interface PourAttachment {
  file_id: string;
  pour_id: string;
  file_name: string;
  file_path: string;
  download_url?: string | null;
}

export interface PourAttachmentListResponse {
  attachments: PourAttachment[];
  total: number;
}

/** The pour's site weather: its weather station (picked on the Hardware
 *  step) and that station's newest reading on the pour. Weather is
 *  pour-level, so a station is never one of the dashboard's `locations`.
 *  Times are naive UTC; wind is km/h. */
export interface SiteWeather {
  station_id: string;
  /** The station location's name; null for a legacy pour that names its
   *  station without a location. */
  name?: string | null;
  code?: string | null;
  /** The pour's Weather Station, the one the weather job feeds. False: a
   *  station location on a pour whose functions do not use weather. */
  linked: boolean;
  at?: string | null;
  temp?: number | null;
  max_temp?: number | null;
  humidity?: number | null;
  wind_speed?: number | null;
  track_status?: string | null;
  starts_at?: string | null;
  logging_ended?: string | null;
  /** Other station locations on the pour: only one feeds it. */
  other_stations?: string[];
}

export interface DashboardResponse {
  pour: PourSummary;
  weather?: Weather;
  /** The pour's weather station and its newest reading; null without one.
   *  Absent on older responses. */
  site_weather?: SiteWeather | null;
  locations: LocationStatus[];
  attachments: Attachment[];
}

/** @deprecated A target-achievement line of ChartDataResponse.xPlotLines,
 *  no longer drawn by the dashboard (achievement markers sit on the curves
 *  instead). */
export interface PlotLine {
  value: number;
  color: string;
  width?: number;
  dashStyle?: string;
  label?: { text: string; rotation?: number; y?: number };
  zIndex?: number;
}

export interface ChartLimit {
  value: number;
  operator: string;
  background_color?: string;
  text_color?: string;
  /** Configured limit name (Purpose), e.g. "Max Temp". */
  name?: string | null;
}

export interface ChartTarget {
  target_id: string;
  target_strength: number;
  target_ea: number;
  achieved: boolean;
  achievement_time?: string;
  /** Configured purpose, e.g. "Initial Stressing"; null for legacy rows. */
  target_label?: string | null;
  /** The pour-level target this location target copies. */
  pour2_target_id?: string | null;
  /** The location's final (highest maturity) target. */
  is_final?: boolean;
  /** Target strength outside the tested range; when absent the UI works it
   *  out from the response's calibrated_range. */
  outside_range?: boolean | 'below' | 'above' | null;
}

export interface CurrentValues {
  temperature?: number;
  max_temperature?: number;
  max_temperature_time?: string;
  strength?: number;
  // Older responses only: the lowest calibrated sample strength, shown as
  // '< min_strength' when `strength` is missing. Newer responses send
  // strength_reading instead.
  min_strength?: number;
  /** Current strength for display (calibrated-range rule). */
  strength_reading?: StrengthReading | null;
  ea?: number;
  evaporation?: number;
  status: string;
  status_color?: string;
  last_reading?: string;
  temp_trend?: string;
}

/** @deprecated Part of ChartDataResponse.progress_bar, no longer drawn by the
 *  dashboard (the Targets card reads ChartDataResponse.target). */
export interface ProgressBarTarget {
  target_id: string;
  /** Configured label (Pour2Target.Purpose, e.g. "Initial Stressing"), or
   *  "Target" without one. Never carries the value: show target_value (company
   *  unit) or the target's maturity. Older responses may send a value-only
   *  name ("25.0 Psi"), which targetPurpose filters out. */
  target_name: string;
  /** Target strength in the company unit; 0 when the target has none. */
  target_value: number;
  percentage: number;
  achieved: boolean;
  /** "YYYY-MM-DD HH:MM:SS" in UTC: show it in the pour's timezone. */
  achieved_time?: string;
  elapsed_time?: string;
  /** Target strength outside the tested range (as ChartTarget). */
  outside_range?: boolean | 'below' | 'above' | null;
}

/** @deprecated ChartDataResponse.progress_bar (the old Target Achievement
 *  bar), no longer drawn by the dashboard; the backend still sends it. */
export interface ProgressBar {
  current_percentage: number;
  /** "24.3 MPa", "< 3.3 MPa", "> 41.0 MPa", "NCS*"; without a usable curve
   *  the maturity, an equivalent age naming its specified temperature
   *  ("120.5 °C·hrs", "EA at 20 °C: 36.2 hrs"). */
  current_display: string;
  /** Tooltip for current_display; null when there is nothing to explain. */
  current_tooltip?: string | null;
  final_target: number;
  targets: ProgressBarTarget[];
  is_complete: boolean;
}

export interface LocationDetails {
  logging_started?: string;
  /** Actual logger stop for this location; absent while still recording. */
  logging_finished?: string;
  /** Planned monitoring start from pour setup. */
  scheduled_start?: string;
  ea_started?: string;
  signal_strength?: string;
  battery_level?: string;
  location_status: string;
  location_status_color: string;
  location_status_text: string;
  logger_number?: string;
  channel_no?: number;
}

export interface TempDifferentialLocation {
  location_id: string;
  location_name: string;
  current_diff?: number;
  current_diff_color?: string;
  current_diff_text_color?: string;
  max_diff?: number;
  max_diff_time?: string;
  max_diff_color?: string;
  max_diff_text_color?: string;
}

export interface ChartDataResponse {
  temp: number[][];
  tankTemp: number[][];
  eva: number[][];
  ea: (number | null)[][];
  difftemp: Record<string, number[][]>;
  limit: ChartLimit[];
  diffLimit: ChartLimit[];
  evaLimit: ChartLimit[];
  target: ChartTarget[];
  /** @deprecated Target-achievement lines, no longer drawn by the dashboard
   *  (still sent by the backend). */
  xPlotLines?: PlotLine[];
  current: CurrentValues;
  /** @deprecated The old Target Achievement bar, no longer drawn by the
   *  dashboard (still sent by the backend). */
  progress_bar?: ProgressBar;
  location_details?: LocationDetails;
  temp_differential_locations: TempDifferentialLocation[];
  location_name?: string;
  logger_code?: string;
  ea_start?: string;
  /** Pour-level milestones for the Event Timeline. */
  pour_start_time?: string;
  pour_created?: string;
  pour_created_by?: string;
  /** '0' pending, '1' in progress, '2' complete. */
  pour_status?: string;
  /** PourInfo.LoggerEnd: when the pour was stopped. */
  pour_stopped?: string;
  /** 'System' (stopped automatically) or 'Manual' (vwPourInfo text). */
  pour_stop_by?: string;
  /** When the pour is set to stop (timed stop cutoff or preset stop time). */
  planned_stop?: string;
  product_strength_grade?: number;
  /** When this location's maturity reached the grade strength (from the
   *  curve); null until reached, without a grade, curve or samples, and when
   *  the grade is outside the tested range. Absent on older responses. */
  grade_strength_time?: string | null;
  /** The grade strength lies outside the tested range. */
  grade_outside_range?: boolean;
  timezone_offset: number;
  /** 'TEMPERATURE' | 'HUMIDITY'; null/undefined = temperature. */
  channel_type?: string | null;
  /** The pour's maturity naming (absent on older responses). */
  maturity?: MaturityDescriptor | null;
  /** The company's strength unit (MPa / Psi / N/mm²). */
  strength_unit?: string | null;
  calibrated_range?: CalibratedRange | null;
  /** Gaps in this location's readings that break ASTM C1074 7.3: the first
   *  few in time order (the list is capped). */
  reading_interval_issues?: ReadingIntervalIssue[] | null;
  /** How many such gaps there are in all. */
  reading_interval_issue_count?: number | null;
  /** getLocationChartSeries with `since`: the since the server applied
   *  (epoch ms, exclusive), so the series hold only newer points. null or
   *  absent: the series are full lists (older backends always send these). */
  since_applied?: number | null;
  /** Fields the server left out of a `since` reply: keep the cached ones. */
  carried?: string[];
}

/** One series of the location charts call ('temp' excludes the tank,
 *  which is 'tank'; 'diff' is the differential to each partner). */
export type ChartSeriesKey = 'temp' | 'tank' | 'eva' | 'ea' | 'diff';

export interface PourLocation {
  LocationID: string;
  LocationName: string;
  PourID: string;
  X?: number;
  Y?: number;
  Z?: number;
  Description?: string;
}

export interface PourDataPoint {
  DataPointID: string;
  PourID: string;
  LocationID: string;
  Timestamp: string;
  Temperature?: number;
  Channel1?: number;
  Channel2?: number;
  Channel3?: number;
  Channel4?: number;
}

/** Counts from POST /pour/{id}/maturity/recalculate. */
export interface MaturityRecalculateResult {
  locations: number;
  locations_recalculated: number;
  points_recalculated: number;
}

// ---------------------------------------------------------------------------
// API Service
// ---------------------------------------------------------------------------

/** GET an .xlsx from the API and hand it to the browser as a file download,
 *  using the server's Content-Disposition filename when present. */
const downloadExcel = async (path: string, fallbackFilename: string): Promise<void> => {
  const response = await apiClient.get(path, { responseType: 'blob' });
  const disposition = (response.headers?.['content-disposition'] as string) || '';
  const match = disposition.match(/filename="([^"]+)"/);
  const filename = match ? match[1] : fallbackFilename;
  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/** Set once the charts call refused a series list (an older backend takes
 *  only 'all' / 'ea'); getLocationChartSeries then asks for those at once. */
let chartSeriesListRefused = false;

/** Whether the backend takes a series list and `since` (as far as known:
 *  true until a call is refused). */
export const chartSeriesListSupported = (): boolean => !chartSeriesListRefused;

/** Tests only: forget a refused series list. */
export const resetChartSeriesSupport = (): void => {
  chartSeriesListRefused = false;
};

/** The older backend's series value for a request: 'ea' alone stays 'ea',
 *  anything else needs 'all'. */
const legacySeries = (series: ChartSeriesKey[] | 'all' | 'ea'): 'all' | 'ea' => {
  if (series === 'all' || series === 'ea') return series;
  return series.length === 1 && series[0] === 'ea' ? 'ea' : 'all';
};

/** The older backend's query for a request: its series value, plus
 *  include_diff=false when a listed request leaves out 'diff' (as desktop's
 *  single-metric charts ask), so 'all' skips the differential work. */
const legacyQuery = (series: ChartSeriesKey[] | 'all' | 'ea'): string => {
  const legacy = legacySeries(series);
  const skipDiff = legacy === 'all' && typeof series !== 'string' && !series.includes('diff');
  return `series=${legacy}${skipDiff ? '&include_diff=false' : ''}`;
};

/** A 422 whose validation errors name the series query parameter. */
const isSeriesRefusal = (error: any): boolean => {
  if (error?.response?.status !== 422) return false;
  const detail = error.response.data?.detail;
  return (
    Array.isArray(detail) &&
    detail.some((item: any) => Array.isArray(item?.loc) && item.loc.includes('series'))
  );
};

export const poursAPI = {
  // ------ Pour CRUD ------

  /** Live pours by default; `archived: true` returns only archived pours.
   *  Every pour, unfiltered: use getPourPage for the control panel list. */
  getPours: async (options?: { archived?: boolean }): Promise<PourListResponse> => {
    const response = await apiClient.get('/pour/', {
      params: options?.archived ? { archived: true } : undefined,
    });
    return response.data;
  },

  /** One page of the control panel list, filtered and paged by the server.
   *  `status` is the list's filter label ("In progress", "Pending",
   *  "Complete" or "All"); `page` is zero-based. */
  getPourPage: async (options: PourPageQuery): Promise<PourPageResponse> => {
    const response = await apiClient.get('/pour/', {
      params: {
        page: options.page,
        page_size: options.pageSize,
        status: options.status,
        search: options.search?.trim() || undefined,
        archived: options.archived || undefined,
        product_id: options.productId || undefined,
        // Only sent when turned off, so the Control Panel keeps its default.
        health: options.health === false ? false : undefined,
        // Only sent when asked for, so other callers' requests are unchanged.
        health_detail: options.healthDetail ? true : undefined,
        company_id: options.companyId || undefined,
      },
    });
    return response.data;
  },

  /** Every pour matching the filters, read a page at a time from the
   *  server, for pickers that list them all (a mix design's pours, the
   *  simulator's live pours). Much smaller than getPours, which returns
   *  every pour the user can see. */
  getAllPourPages: async (
    options: Omit<PourPageQuery, 'page' | 'pageSize'>,
  ): Promise<Pour[]> => {
    // Large pages and no logger health roll-up (pickers never show it, and
    // it is the slow part of the request).
    const pageSize = 1000;
    const all: Pour[] = [];
    for (let page = 0; ; page += 1) {
      const res = await poursAPI.getPourPage({ ...options, page, pageSize, health: false });
      all.push(...(res.pours || []));
      if (!res.pours?.length || all.length >= res.total) return all;
    }
  },

  /** The project names the control panel map pins and the newest pour's
   *  project, for the whole filtered list (not just the page shown). */
  getPourMapSummary: async (
    options: Omit<PourPageQuery, 'page' | 'pageSize'>,
  ): Promise<PourMapSummary> => {
    const response = await apiClient.get('/pour/map-summary', {
      params: {
        status: options.status,
        search: options.search?.trim() || undefined,
        archived: options.archived || undefined,
        company_id: options.companyId || undefined,
      },
    });
    return response.data;
  },

  /** Superadmin only: live In Progress pours per client, by company id. */
  getClientInProgressCounts: async (): Promise<Record<string, number>> => {
    const response = await apiClient.get('/pour/client-in-progress-counts');
    return response.data?.counts ?? {};
  },

  getPourById: async (pourId: string): Promise<Pour> => {
    const response = await apiClient.get(`/pour/${pourId}`);
    return response.data;
  },

  getPourDetail: async (pourId: string): Promise<PourDetail> => {
    const response = await apiClient.get(`/pour/${pourId}`);
    return response.data;
  },

  createPour: async (data: PourCreate): Promise<PourCreateResponse> => {
    const response = await apiClient.post('/pour/', data);
    return response.data;
  },

  updatePour: async (pourId: string, data: PourUpdate): Promise<PourDetail> => {
    const response = await apiClient.put(`/pour/${pourId}`, data);
    return response.data;
  },

  /** ASTM C1074 9.2 check for a pour that would be created with this mix
   *  design and calibration (null: the mix design's active, finished one). */
  checkCalibration: async (
    productId: string,
    calibrationId?: string | null,
  ): Promise<CalibrationCheck> => {
    const response = await apiClient.post('/pour/calibration-check', {
      product_id: productId,
      calibration_id: calibrationId ?? null,
    });
    return response.data;
  },

  /**
   * Archive (soft-delete) a pour. Admin roles only. Pending pours are
   * deleted, Completed pours are archived, In Progress pours are refused
   * until stopped. Recorded data is retained; see `restorePour`.
   */
  archivePour: async (pourId: string): Promise<{ PourID: string; action: 'deleted' | 'archived' }> => {
    const response = await apiClient.delete(`/pour/${pourId}`);
    return response.data;
  },

  /** Bring an archived pour back into the live lists (admin roles only). */
  restorePour: async (pourId: string): Promise<{ PourID: string; restored: boolean }> => {
    const response = await apiClient.post(`/pour/${pourId}/restore`);
    return response.data;
  },

  /**
   * Manually stop a running pour. Idempotent — already-stopped pours
   * return `already_stopped: true` without side effects.
   */
  stopPour: async (pourId: string): Promise<{
    PourID: string;
    Status: string;
    StopBy: string | null;
    LoggerEnd: string | null;
    already_stopped: boolean;
    tanks_notified: number;
  }> => {
    const response = await apiClient.post(`/pour/${pourId}/stop`);
    return response.data;
  },

  /** Lock a pour against edits (admin roles only). */
  lockPour: async (pourId: string): Promise<{ PourID: string; Locked: boolean }> => {
    const response = await apiClient.post(`/pour/${pourId}/lock`);
    return response.data;
  },

  /** Unlock a completed pour so changes can be made (admin roles only). */
  unlockPour: async (pourId: string): Promise<{ PourID: string; Locked: boolean }> => {
    const response = await apiClient.post(`/pour/${pourId}/unlock`);
    return response.data;
  },

  getLookups: async (pourId?: string): Promise<PourLookups> => {
    // Pass pourId when editing an existing pour so its currently-assigned
    // (possibly inactive) limit group stays in the dropdown.
    const response = await apiClient.get('/pour/lookups', {
      params: pourId ? { pour_id: pourId } : undefined,
    });
    return response.data;
  },

  // ------ Targets ------

  getPourTargets: async (pourId: string): Promise<Pour2Target[]> => {
    const response = await apiClient.get(`/pour/${pourId}/targets`);
    return (response.data.targets ?? []).map(toPour2Target);
  },

  addPourTarget: async (pourId: string, data: Pour2TargetCreate): Promise<Pour2Target> => {
    const response = await apiClient.post(`/pour/${pourId}/targets`, data);
    return toPour2Target(response.data);
  },

  updatePourTarget: async (pourId: string, targetId: string, data: Partial<Pour2TargetCreate>): Promise<Pour2Target> => {
    const response = await apiClient.put(`/pour/${pourId}/targets/${targetId}`, data);
    return toPour2Target(response.data);
  },

  deletePourTarget: async (pourId: string, targetId: string): Promise<void> => {
    await apiClient.delete(`/pour/${pourId}/targets/${targetId}`);
  },

  // ------ Limits (Mass Concrete) ------

  getPourLimits: async (pourId: string): Promise<Pour2Limit[]> => {
    const response = await apiClient.get(`/pour/${pourId}/limits`);
    return response.data.limits;
  },

  addPourLimit: async (pourId: string, data: Pour2LimitCreate): Promise<Pour2Limit> => {
    const response = await apiClient.post(`/pour/${pourId}/limits`, data);
    return response.data;
  },

  updatePourLimit: async (pourId: string, limitId: string, data: Partial<Pour2LimitCreate>): Promise<Pour2Limit> => {
    const response = await apiClient.put(`/pour/${pourId}/limits/${limitId}`, data);
    return response.data;
  },

  deletePourLimit: async (pourId: string, limitId: string): Promise<void> => {
    await apiClient.delete(`/pour/${pourId}/limits/${limitId}`);
  },

  // ------ Evaporation Limits ------

  getPourEvaporationLimits: async (pourId: string): Promise<Pour2EvaporationLimit[]> => {
    const response = await apiClient.get(`/pour/${pourId}/evaporation-limits`);
    return response.data.evaporation_limits;
  },

  addPourEvaporationLimit: async (pourId: string, data: Pour2EvaporationLimitCreate): Promise<Pour2EvaporationLimit> => {
    const response = await apiClient.post(`/pour/${pourId}/evaporation-limits`, data);
    return response.data;
  },

  updatePourEvaporationLimit: async (pourId: string, limitId: string, data: Partial<Pour2EvaporationLimitCreate>): Promise<Pour2EvaporationLimit> => {
    const response = await apiClient.put(`/pour/${pourId}/evaporation-limits/${limitId}`, data);
    return response.data;
  },

  deletePourEvaporationLimit: async (pourId: string, limitId: string): Promise<void> => {
    await apiClient.delete(`/pour/${pourId}/evaporation-limits/${limitId}`);
  },

  // ------ Contacts ------

  getPourContacts: async (pourId: string): Promise<Pour2Contact[]> => {
    const response = await apiClient.get(`/pour/${pourId}/contacts`);
    return response.data.contacts;
  },

  addPourContact: async (pourId: string, data: Pour2ContactCreate): Promise<Pour2Contact> => {
    const response = await apiClient.post(`/pour/${pourId}/contacts`, data);
    return response.data;
  },

  updatePourContact: async (pourId: string, contactId: string, data: Partial<Pour2ContactCreate>): Promise<Pour2Contact> => {
    const response = await apiClient.put(`/pour/${pourId}/contacts/${contactId}`, data);
    return response.data;
  },

  deletePourContact: async (pourId: string, contactId: string): Promise<void> => {
    await apiClient.delete(`/pour/${pourId}/contacts/${contactId}`);
  },

  // ------ Monitoring Locations ------

  getPourLocations2: async (pourId: string): Promise<PourLocation2[]> => {
    const response = await apiClient.get(`/pour/${pourId}/locations2`);
    return response.data.locations;
  },

  addPourLocation2: async (pourId: string, data: PourLocation2Create): Promise<PourLocation2> => {
    const response = await apiClient.post(`/pour/${pourId}/locations2`, data);
    return response.data;
  },

  /** Add several locations in one all-or-nothing request (1 to 100 rows),
   *  e.g. every channel of a ConXagent. Returns the created rows. */
  addPourLocations2Bulk: async (pourId: string, locations: PourLocation2Create[]): Promise<PourLocation2[]> => {
    const response = await apiClient.post(`/pour/${pourId}/locations2/bulk`, { locations });
    return response.data.locations;
  },

  updatePourLocation2: async (pourId: string, locationId: string, data: Partial<PourLocation2Create>): Promise<PourLocation2> => {
    const response = await apiClient.put(`/pour/${pourId}/locations2/${locationId}`, data);
    return response.data;
  },

  deletePourLocation2: async (pourId: string, locationId: string): Promise<void> => {
    await apiClient.delete(`/pour/${pourId}/locations2/${locationId}`);
  },

  getLocationTargets: async (pourId: string, locationId: string): Promise<LocationTarget[]> => {
    const response = await apiClient.get(`/pour/${pourId}/locations2/${locationId}/targets`);
    return response.data;
  },

  // ------ Legacy endpoints ------

  getPoursByProject: async (projectId: string): Promise<Pour[]> => {
    const response = await apiClient.get(`/pour/project/${projectId}`);
    return response.data;
  },

  getPourLocations: async (pourId: string): Promise<PourLocation[]> => {
    const response = await apiClient.get(`/pour/${pourId}/locations`);
    return response.data;
  },

  getPourDataPoints: async (pourId: string): Promise<PourDataPoint[]> => {
    const response = await apiClient.get(`/pour/${pourId}/datapoints`);
    return response.data;
  },

  /** The dashboard summary. `siteState` adds each location's limit state,
   *  breaches, differentials and evaporation (the phone layout); without
   *  it the request is exactly as before. */
  getDashboardData: async (
    pourId: string,
    opts?: { siteState?: boolean },
  ): Promise<DashboardResponse> => {
    const response = opts?.siteState
      ? await apiClient.get<DashboardResponse>(`/pour/${pourId}/dashboard`, {
          params: { site_state: true },
        })
      : await apiClient.get<DashboardResponse>(`/pour/${pourId}/dashboard`);
    return response.data;
  },

  /** The pour's weather station readings; with `since` (epoch ms UTC,
   *  exclusive) only those after it. */
  getPourWeather: async (pourId: string, since?: number): Promise<WeatherSeries> => {
    const response = await apiClient.get<WeatherSeries>(`/pour/${pourId}/weather`, {
      params: since !== undefined ? { since } : undefined,
    });
    return response.data;
  },

  /** Download the pour's temperature readings as an .xlsx sheet. */
  exportTemperatureData: async (pourId: string): Promise<void> =>
    downloadExcel(`/pour/${pourId}/temperature/export`, `PourLoggerTemp-${pourId}.xlsx`),

  /** Download the pour's strength / maturity readings (the data behind the
   *  Strength Charts section) as an .xlsx sheet. */
  exportStrengthData: async (pourId: string): Promise<void> =>
    downloadExcel(`/pour/${pourId}/strength/export`, `PourLoggerStrength-${pourId}.xlsx`),

  simulateData: async (pourId: string, options?: {
    mode?: string;
    hours?: number;
    interval_minutes?: number;
    base_temp?: number;
    peak_temp?: number;
    peak_hour_percent?: number;
    noise?: number;
    realtime_interval_seconds?: number;
    realtime_duration_minutes?: number;
    include_weather?: boolean;
    weather_temp?: number;
    weather_humidity?: number;
    weather_wind?: number;
    start_time?: string;
    /** Channel number → 'core' | 'surface' | 'ambient' | 'auto' */
    location_roles?: Record<string, string>;
  }): Promise<{ message: string; datapoints_created: number; live?: boolean }> => {
    const response = await apiClient.post(`/pour/${pourId}/simulate`, options || {});
    return response.data;
  },

  /** Super admin only: stop a realtime simulator replay (readings written so far stay). */
  stopSimulation: async (pourId: string): Promise<{ message: string; stopped: boolean }> => {
    const response = await apiClient.post(`/pour/${pourId}/simulate/stop`);
    return response.data;
  },

  /** Super admin only: one-click test pour wired to the virtual logger. */
  quickCreateSimPour: async (options: {
    project_id: string;
    kind: 'temp_only' | 'maturity';
    num_locations?: number;
    hours?: number;
    pour_name?: string;
    product_id?: string;
  }): Promise<{ pour_id: string; pour_code: string; message: string }> => {
    const response = await apiClient.post('/pour/simulator/quick-pour', options);
    return response.data;
  },

  getLocationChartData: async (
    pourId: string,
    locationId: string,
    loggerId?: string,
    channelNo?: number,
    /** false skips the (expensive) temperature-differential computation on
     *  the backend — use when only plotting a single metric per location. */
    includeDiff?: boolean,
    /** 'ea' returns only the EA/strength series, skipping the temperature /
     *  tank / evaporation fetches (incl. the slow Cosmos read on completed
     *  pours). Use for strength/maturity-only charts. */
    series?: 'all' | 'ea'
  ): Promise<ChartDataResponse> => {
    const params = new URLSearchParams();
    if (loggerId) params.append('logger_id', loggerId);
    if (channelNo !== undefined) params.append('channel_no', channelNo.toString());
    if (includeDiff === false) params.append('include_diff', 'false');
    if (series && series !== 'all') params.append('series', series);
    const url = `/pour/${pourId}/location/${locationId}/charts${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await apiClient.get<ChartDataResponse>(url);
    return response.data;
  },

  /** Location chart data for the phone layout: only the listed series, and
   *  with `since` (epoch ms UTC, exclusive) only the points after it (the
   *  reply's since_applied says so; null or absent means full lists).
   *  `series` 'all' / 'ea' ask as getLocationChartData does. An older
   *  backend refuses the list form with a 422: the call then asks for 'all'
   *  (or 'ea' when only 'ea' was listed) without since, here and on every
   *  later call (chartSeriesListSupported); 'all' adds include_diff=false
   *  unless 'diff' was listed. */
  getLocationChartSeries: async (
    pourId: string,
    locationId: string,
    opts: { series: ChartSeriesKey[] | 'all' | 'ea'; since?: number | null; signal?: AbortSignal },
  ): Promise<ChartDataResponse> => {
    const base = `/pour/${pourId}/location/${locationId}/charts`;
    const config = opts.signal ? { signal: opts.signal } : undefined;
    const fallback = `${base}?${legacyQuery(opts.series)}`;
    if (typeof opts.series !== 'string' && chartSeriesListRefused) {
      const response = await apiClient.get<ChartDataResponse>(fallback, config);
      return response.data;
    }
    // Built by hand so the list reads series=temp,tank (no %2C); the values
    // come from a fixed set.
    const query = [
      `series=${typeof opts.series === 'string' ? opts.series : opts.series.join(',')}`,
    ];
    if (typeof opts.since === 'number' && Number.isFinite(opts.since)) {
      query.push(`since=${Math.floor(opts.since)}`);
    }
    try {
      const response = await apiClient.get<ChartDataResponse>(`${base}?${query.join('&')}`, config);
      return response.data;
    } catch (error) {
      if (typeof opts.series === 'string' || !isSeriesRefusal(error)) throw error;
      chartSeriesListRefused = true;
      const response = await apiClient.get<ChartDataResponse>(fallback, config);
      return response.data;
    }
  },

  /** Set a location's concrete start (epoch ms) and recalculate its maturity
   *  from that time. Targets already met are marked silently. */
  resetEAStart: async (locationId: string, eaStart: number): Promise<void> => {
    await apiClient.post('/pour/ea-reset', {
      location_id: locationId,
      ea_start: eaStart
    });
  },

  /** Recalculate maturity from the recorded temperatures for every location
   *  of the pour, or one location. No emails are sent. Refused when locked. */
  recalculateMaturity: async (pourId: string, locationId?: string): Promise<MaturityRecalculateResult> => {
    const response = await apiClient.post<MaturityRecalculateResult>(
      `/pour/${pourId}/maturity/recalculate`,
      locationId ? { location_id: locationId } : {},
    );
    return response.data;
  },

  // ------ Attachments ------

  listPourAttachments: async (pourId: string): Promise<PourAttachment[]> => {
    const response = await apiClient.get<PourAttachmentListResponse>(
      `/pour/${pourId}/attachments`,
    );
    return response.data.attachments;
  },

  uploadPourAttachment: async (pourId: string, file: File): Promise<PourAttachment> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<PourAttachment>(
      `/pour/${pourId}/attachments`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  downloadPourAttachmentUrl: (pourId: string, fileId: string): string =>
    `/pour/${pourId}/attachments/${fileId}/download`,

  deletePourAttachment: async (pourId: string, fileId: string): Promise<void> => {
    await apiClient.delete(`/pour/${pourId}/attachments/${fileId}`);
  },
};
